import { useCallback, useEffect, useState, type ChangeEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { IconEye as Eye, IconEyeOff as EyeClosed } from '@tabler/icons-react';
import {
  AlertDialog,
  Button,
  Description,
  FieldError,
  Input as HeroUiInput,
  InputGroup,
  Label,
  Radio,
  RadioGroup,
  TextField,
  toast,
} from '@heroui/react';
import { trpc } from '@/lib/trpc';
import { useAppDispatch } from '@/stores/hooks';
import { setCredentials } from '@hs/shared';
import { handleTRPCError } from '@/utils/error-handler';
import { handleError } from '@hs/shared';

type SpaceType = 'PERSONAL' | 'TEAM' | 'ENTERPRISE';
type EnterpriseType = 'CORP_HEAD' | 'SUB_COMPANY' | 'BRANCH_ORG';
type Step = 1 | 2 | 3;

const SPACE_OPTIONS: { value: SpaceType; label: string }[] = [
  { value: 'PERSONAL', label: '个人' },
  { value: 'TEAM', label: '团队' },
  { value: 'ENTERPRISE', label: '企业' },
];

const ENTERPRISE_OPTIONS: { value: EnterpriseType; label: string }[] = [
  { value: 'CORP_HEAD', label: '总公司' },
  { value: 'SUB_COMPANY', label: '子公司' },
  { value: 'BRANCH_ORG', label: '分支机构' },
];

const SMS_COOLDOWN_SECONDS = 60;

function Input(props: Record<string, unknown>) {
  return <HeroUiInput {...(props as object)} />;
}
const MOBILE_RE = /^1[3-9]\d{9}$/;
const USERNAME_RE = /^[a-zA-Z][a-zA-Z0-9_]{2,}$/;
const PASSWORD_RULES = [
  { test: (v: string) => v.length >= 6, msg: '不少于6位' },
  { test: (v: string) => /[a-z]/.test(v), msg: '须包含小写字母' },
  { test: (v: string) => /[A-Z]/.test(v), msg: '须包含大写字母' },
  { test: (v: string) => /\d/.test(v), msg: '须包含数字' },
  { test: (v: string) => /[^a-zA-Z0-9]/.test(v), msg: '须包含特殊符号' },
];

function passwordError(password: string): string | null {
  for (const rule of PASSWORD_RULES) {
    if (!rule.test(password)) return rule.msg;
  }
  return null;
}

function parseSpaceTypeParam(value: string | null): SpaceType | null {
  if (value === 'PERSONAL' || value === 'TEAM' || value === 'ENTERPRISE') {
    return value;
  }
  return null;
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const [step, setStep] = useState<Step>(1);
  const [spaceType, setSpaceType] = useState<SpaceType>(
    () => parseSpaceTypeParam(searchParams.get('space-type')) ?? 'PERSONAL'
  );
  const [enterpriseType, setEnterpriseType] = useState<EnterpriseType>('CORP_HEAD');
  const [spaceName, setSpaceName] = useState('');
  const [mobile, setMobile] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [nickname, setNickname] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const [mobileError, setMobileError] = useState<string | null>(null);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [passwordErrorText, setPasswordErrorText] = useState<string | null>(null);
  const [spaceError, setSpaceError] = useState<string | null>(null);

  const [mobileRegisteredOpen, setMobileRegisteredOpen] = useState(false);
  const [smsCooldown, setSmsCooldown] = useState(0);
  const [canSendSms, setCanSendSms] = useState(false);

  useEffect(() => {
    if (smsCooldown <= 0) return;
    const t = window.setTimeout(() => setSmsCooldown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [smsCooldown]);

  const [smsVerifying, setSmsVerifying] = useState(false);

  const trpcUtils = trpc.useUtils();

  const sendSmsMutation = trpc.auth.sendSmsCode.useMutation({
    onSuccess: () => {
      setSmsCooldown(SMS_COOLDOWN_SECONDS);
      toast.success('验证码已发送');
    },
    onError: (error) => handleTRPCError(error),
  });

  const checkSmsMutation = trpc.auth.checkSmsCode.useMutation();

  const registerMutation = trpc.auth.registerV2.useMutation({
    onSuccess: (data) => {
      dispatch(
        setCredentials({
          user: {
            id: data.user.id,
            username: data.user.username,
            nickname: data.user.nickname ?? undefined,
            email: data.user.email ?? undefined,
            mobile: data.user.mobile ?? undefined,
            avatarUrl: data.user.avatarUrl ?? undefined,
            status: data.user.status ?? undefined,
            roles: data.user.roles ?? undefined,
            apps: data.user.apps ?? undefined,
            spaces: data.user.spaces ?? undefined,
          },
          token: data.accessToken,
          refreshToken: data.refreshToken,
        })
      );
      toast.success('注册成功', {
        description: `欢迎加入，${data.user.nickname || data.user.username}！`,
      });
      navigate('/');
    },
    onError: (error) => handleTRPCError(error),
  });

  const validateMobileOnComplete = useCallback(
    async (value: string, options?: { openDialog?: boolean }) => {
      if (!MOBILE_RE.test(value)) {
        setMobileError('手机号格式有误，请修正');
        setCanSendSms(false);
        return false;
      }
      setMobileError(null);
      try {
        const data = await trpcUtils.auth.checkMobileAvailable.fetch({ mobile: value });
        if (!data.available) {
          setMobileError('手机号已注册，请换一个');
          if (options?.openDialog !== false) {
            setMobileRegisteredOpen(true);
          }
          setCanSendSms(false);
          return false;
        }
        setCanSendSms(true);
        return true;
      } catch {
        setCanSendSms(false);
        return false;
      }
    },
    [trpcUtils]
  );

  const handleMobileChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    setMobile(digits);
    setMobileError(null);
    setCanSendSms(false);
    if (digits.length === 11) {
      void validateMobileOnComplete(digits);
    }
  };

  const handleSendSms = async () => {
    if (!canSendSms || smsCooldown > 0) return;
    await sendSmsMutation.mutateAsync({ mobile, scene: 'REGISTER' });
  };

  const goStep2 = () => {
    setSpaceError(null);
    if (!spaceName.trim()) {
      setSpaceError('请填写空间名称');
      return;
    }
    if (spaceType === 'ENTERPRISE' && !enterpriseType) {
      setSpaceError('请选择企业类型');
      return;
    }
    setStep(2);
  };

  const goStep3 = async () => {
    setSmsError(null);
    if (!MOBILE_RE.test(mobile)) {
      setMobileError('手机号格式有误，请修正');
      return;
    }
    const mobileOk = await validateMobileOnComplete(mobile, { openDialog: false });
    if (!mobileOk) return;
    if (!smsCode.trim()) {
      setSmsError('请输入验证码');
      return;
    }
    setSmsVerifying(true);
    try {
      await checkSmsMutation.mutateAsync({
        mobile,
        code: smsCode.trim(),
        scene: 'REGISTER',
      });
      setStep(3);
    } catch (error) {
      const { message } = handleError(error);
      setSmsError(message || '验证码有误，请检查');
    } finally {
      setSmsVerifying(false);
    }
  };

  const handleUsernameBlur = async () => {
    const u = username.trim();
    if (!u) {
      setUsernameError('用户名不能为空');
      return;
    }
    if (!USERNAME_RE.test(u)) {
      setUsernameError('仅限英文、数字、下划线，英文开头，不少于3位');
      return;
    }
    try {
      const data = await trpcUtils.auth.checkUsernameAvailable.fetch({ username: u });
      if (!data.available) {
        setUsernameError('用户名已被注册，请更换后重试');
        return;
      }
      setUsernameError(null);
    } catch {
      setUsernameError('用户名校验失败，请稍后重试');
    }
  };

  const handlePasswordBlur = () => {
    const err = passwordError(password);
    setPasswordErrorText(err);
  };

  const handleSubmit = () => {
    if (!nickname.trim()) {
      toast.danger('请填写昵称');
      return;
    }
    if (usernameError || passwordErrorText) return;
    if (!USERNAME_RE.test(username.trim())) {
      setUsernameError('请检查用户名格式');
      return;
    }
    const pwdErr = passwordError(password);
    if (pwdErr) {
      setPasswordErrorText(pwdErr);
      return;
    }

    registerMutation.mutate({
      source: 'WEB',
      space: {
        type: spaceType,
        name: spaceName.trim(),
        ...(spaceType === 'ENTERPRISE' ? { enterpriseType } : {}),
      },
      mobile,
      smsCode: smsCode.trim(),
      username: username.trim(),
      password,
      nickname: nickname.trim(),
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-default-50 to-default-100 p-4">
      <div className="w-full max-w-sm">
        <div className="rounded-2xl border border-default-200 bg-content1 p-8 shadow-sm">
          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-foreground">注册人文派账号</h1>
            <p className="mt-3 text-sm text-default-600">
              已有账号，
              <Link
                to="/login"
                className="font-medium text-accent underline-offset-2 hover:underline"
              >
                立即登录
              </Link>
            </p>
          </div>

          {step === 1 && (
            <div className="space-y-6">
              <div className="space-y-3">
                <p className="text-sm font-medium text-foreground">选择空间类型</p>
                <RadioGroup
                  value={spaceType}
                  onChange={(v) => setSpaceType(v as SpaceType)}
                  orientation="horizontal"
                  className="flex flex-wrap gap-4"
                  variant="secondary"
                >
                  {SPACE_OPTIONS.map((opt) => (
                    <Radio key={opt.value} value={opt.value}>
                      <Radio.Content>
                        <Radio.Control>
                          <Radio.Indicator />
                        </Radio.Control>
                        <Label>{opt.label}</Label>
                      </Radio.Content>
                    </Radio>
                  ))}
                </RadioGroup>
              </div>

              {spaceType === 'ENTERPRISE' && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-foreground">选择企业类型</p>
                  <RadioGroup
                    value={enterpriseType}
                    onChange={(v) => setEnterpriseType(v as EnterpriseType)}
                    orientation="horizontal"
                    className="flex flex-wrap gap-4"
                    variant="secondary"
                  >
                    {ENTERPRISE_OPTIONS.map((opt) => (
                      <Radio key={opt.value} value={opt.value}>
                        <Radio.Content>
                          <Radio.Control>
                            <Radio.Indicator />
                          </Radio.Control>
                          <Label>{opt.label}</Label>
                        </Radio.Content>
                      </Radio>
                    ))}
                  </RadioGroup>
                </div>
              )}

              <TextField className="w-full flex flex-col gap-1" isInvalid={!!spaceError}>
                <Label className="text-sm font-medium">设定空间名称</Label>
                <Input
                  placeholder="为空间设定一个名称"
                  variant="secondary"
                  value={spaceName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSpaceName(e.target.value)}
                />
                <Description className="text-xs text-default-500">
                  给空间设定一个喜欢的名称
                </Description>
                {spaceError ? <FieldError>{spaceError}</FieldError> : null}
              </TextField>

              <Button variant="primary" className="w-full" size="lg" onPress={goStep2}>
                下一步
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <TextField className="w-full flex flex-col gap-1" isInvalid={!!mobileError}>
                <Label className="text-sm font-medium">手机号</Label>
                <Input
                  placeholder="请输入11位手机号"
                  variant="secondary"
                  inputMode="numeric"
                  value={mobile}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleMobileChange(e.target.value)}
                />
                {mobileError ? <FieldError>{mobileError}</FieldError> : null}
              </TextField>

              <TextField className="w-full flex flex-col gap-1" isInvalid={!!smsError}>
                <Label className="text-sm font-medium">验证码</Label>
                <InputGroup aria-label="短信验证码" className="w-full" variant="secondary">
                  <InputGroup.Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="输入验证码"
                    value={smsCode}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                      setSmsError(null);
                    }}
                    autoComplete="one-time-code"
                  />
                  <InputGroup.Suffix className="px-1">
                    <button
                      type="button"
                      className="shrink-0 whitespace-nowrap px-2 text-sm font-medium text-primary disabled:cursor-not-allowed disabled:text-default-400"
                      disabled={
                        !canSendSms || smsCooldown > 0 || sendSmsMutation.isPending || smsVerifying
                      }
                      onClick={() => void handleSendSms()}
                    >
                      {sendSmsMutation.isPending
                        ? '发送中…'
                        : smsCooldown > 0
                          ? `${smsCooldown}s`
                          : '获取验证码'}
                    </button>
                  </InputGroup.Suffix>
                </InputGroup>
                {smsError ? <FieldError>{smsError}</FieldError> : null}
              </TextField>

              <div className="flex gap-3">
                <Button variant="tertiary" className="flex-1" onPress={() => setStep(1)}>
                  上一步
                </Button>
                <Button
                  variant="primary"
                  className="flex-1"
                  isDisabled={smsVerifying}
                  onPress={() => void goStep3()}
                >
                  {smsVerifying ? '验证中…' : '下一步'}
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <TextField className="w-full flex flex-col gap-1" isInvalid={!!usernameError}>
                <Label className="text-sm font-medium">用户名</Label>
                <Input
                  placeholder="输入用户名"
                  variant="secondary"
                  autoComplete="username"
                  value={username}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
                  onBlur={() => void handleUsernameBlur()}
                />
                <Description className="text-xs text-default-500">
                  不少于3位，仅限英文、数字、下划线，英文开头
                </Description>
                {usernameError ? <FieldError>{usernameError}</FieldError> : null}
              </TextField>

              <TextField className="w-full flex flex-col gap-1" isInvalid={!!passwordErrorText}>
                <Label className="text-sm font-medium">密码</Label>
                <InputGroup aria-label="密码" className="w-full" variant="secondary">
                  <InputGroup.Input
                    type={isPasswordVisible ? 'text' : 'password'}
                    placeholder="输入密码"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    onBlur={handlePasswordBlur}
                  />
                  <InputGroup.Suffix className="px-1">
                    <button
                      type="button"
                      onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                      className="text-default-500"
                      aria-label="切换密码可见性"
                    >
                      {isPasswordVisible ? (
                        <EyeClosed className="h-4 w-4" stroke={1.5} />
                      ) : (
                        <Eye className="h-4 w-4" stroke={1.5} />
                      )}
                    </button>
                  </InputGroup.Suffix>
                </InputGroup>
                <Description className="text-xs text-default-500">
                  不少于6位，须包含大小写字母、数字及特殊符号
                </Description>
                {passwordErrorText ? <FieldError>{passwordErrorText}</FieldError> : null}
              </TextField>

              <TextField className="w-full flex flex-col gap-1">
                <Label className="text-sm font-medium">昵称</Label>
                <Input
                  placeholder="为自己设置一个昵称"
                  variant="secondary"
                  value={nickname}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setNickname(e.target.value)}
                />
                <Description className="text-xs text-default-500">为自己设置一个昵称</Description>
              </TextField>

              <div className="flex gap-3">
                <Button variant="tertiary" className="flex-1" onPress={() => setStep(2)}>
                  上一步
                </Button>
                <Button
                  variant="primary"
                  className="flex-1"
                  isDisabled={registerMutation.isPending}
                  onPress={handleSubmit}
                >
                  {registerMutation.isPending ? '注册中…' : '完成注册'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AlertDialog isOpen={mobileRegisteredOpen} onOpenChange={setMobileRegisteredOpen}>
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>提示</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                <p className="text-default-600">手机号已注册，可直接登录后进入空间</p>
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="secondary" onPress={() => setMobileRegisteredOpen(false)}>
                  取消
                </Button>
                <Button variant="primary" onPress={() => navigate('/login')}>
                  去登录
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
}
